import { Plus, Trash2 } from "lucide-react";
import { cn } from "@/lib/cn";
import { Input, Label } from "@/components/ui-kit/Input";
import { Button } from "@/components/ui-kit/Button";
import { useFunder } from "@/funder/FunderContext";

function ToggleChip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={cn(
        "rounded-md border px-3 py-1.5 text-sm font-medium font-sans transition-colors",
        active
          ? "border-primary/40 bg-primary/10 text-primary"
          : "border-border bg-surface text-muted-foreground hover:bg-accent"
      )}
    >
      {children}
    </button>
  );
}

function Dial({ id, label, suffix, value, onChange, error, testid }) {
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input
          id={id}
          type="number"
          step="any"
          data-testid={testid}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={cn(error && "border-destructive focus-visible:ring-destructive", suffix && "pr-9")}
        />
        {suffix && (
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
            {suffix}
          </span>
        )}
      </div>
      {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
    </div>
  );
}

export function CoverageEditor({ profile, rounds, errors, onProfileChange, onRoundsChange }) {
  const { abbr } = useFunder();
  const setRound = (idx, key) => {
    const next = rounds.map((r, i) => (i === idx ? { ...r, [key]: !r[key] } : r));
    onRoundsChange(next);
  };
  const addRound = () => {
    if (rounds.length >= 2) return;
    onRoundsChange([
      ...rounds,
      { order: rounds.length + 1, l1_enabled: true, l2_enabled: true, l3_enabled: true },
    ]);
  };
  const removeRound = (idx) => {
    const next = rounds
      .filter((_, i) => i !== idx)
      .map((r, i) => ({ ...r, order: i + 1 }));
    onRoundsChange(next);
  };

  return (
    <div className="space-y-6" data-testid="coverage-editor">
      {/* Rounds */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <h4 className="font-sans text-sm font-semibold text-foreground">Coverage rounds</h4>
          {rounds.length < 2 && (
            <Button type="button" size="sm" variant="secondary" onClick={addRound} data-testid="add-round-button">
              <Plus className="h-4 w-4" /> Add 2nd round
            </Button>
          )}
        </div>
        <p className="mb-3 text-xs text-muted-foreground">
          Each round is a fresh full allocation (L1 → L2 → L3). Fees spill into a later
          round only after the prior one is exhausted; anything past the last round goes
          to the client at 100%.
        </p>
        <div className="space-y-2">
          {rounds.map((r, idx) => (
            <div
              key={idx}
              className="flex items-center gap-3 rounded-md border border-border bg-surface px-3 py-2"
              data-testid={`round-row-${idx + 1}`}
            >
              <span className="w-16 text-sm font-medium text-foreground">Round {idx + 1}</span>
              <div className="flex flex-wrap gap-2">
                <ToggleChip active={r.l1_enabled} onClick={() => setRound(idx, "l1_enabled")} testid={`round-${idx + 1}-l1`}>
                  L1
                </ToggleChip>
                <ToggleChip active={r.l2_enabled} onClick={() => setRound(idx, "l2_enabled")} testid={`round-${idx + 1}-l2`}>
                  L2
                </ToggleChip>
                <ToggleChip active={r.l3_enabled} onClick={() => setRound(idx, "l3_enabled")} testid={`round-${idx + 1}-l3`}>
                  L3
                </ToggleChip>
              </div>
              {idx > 0 && (
                <button
                  type="button"
                  onClick={() => removeRound(idx)}
                  className="ml-auto rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-destructive"
                  aria-label="Remove round"
                  data-testid={`remove-round-${idx + 1}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Six dials */}
      <div>
        <h4 className="mb-3 font-sans text-sm font-semibold text-foreground">Coverage profile</h4>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Dial id="l1-free" label="L1 free hours" value={profile.l1_free_hours} testid="dial-l1-free-hours"
            onChange={(v) => onProfileChange("l1_free_hours", v)} error={errors.l1_free_hours} />
          <Dial id="l1-pct" label="L1 percent" suffix="%" value={profile.l1_percent} testid="dial-l1-percent"
            onChange={(v) => onProfileChange("l1_percent", v)} error={errors.l1_percent} />
          <Dial id="l2-pct" label="L2 percent" suffix="%" value={profile.l2_percent} testid="dial-l2-percent"
            onChange={(v) => onProfileChange("l2_percent", v)} error={errors.l2_percent} />
          <Dial id="l2-cap" label={`L2 cap (${abbr} contribution)`} suffix="$" value={profile.l2_cap} testid="dial-l2-cap"
            onChange={(v) => onProfileChange("l2_cap", v)} error={errors.l2_cap} />
          <Dial id="l3-pct" label="L3 percent" suffix="%" value={profile.l3_percent} testid="dial-l3-percent"
            onChange={(v) => onProfileChange("l3_percent", v)} error={errors.l3_percent} />
          <Dial id="l3-cap" label={`L3 cap (${abbr} contribution)`} suffix="$" value={profile.l3_cap} testid="dial-l3-cap"
            onChange={(v) => onProfileChange("l3_cap", v)} error={errors.l3_cap} />
        </div>
      </div>
    </div>
  );
}
